/** @jsxImportSource ./simboluri-jsx */
import { t, useT } from "../i18n";
// Diagrame explicative pentru verificările de fundație — desenate, nu doar
// descrise în text. Fiecare arată MECANISMUL pe care îl verifică formula:
// ce împinge, ce ține, unde cedează. Sunt mici (se pun lângă tabel/card) și
// folosesc culorile aplicației: gri = structura, portocaliu = fundația,
// roșu = solicitarea, verde = rezistența.

const S = {
  beton: "#c4c4c4",
  betonLinie: "#858585",
  fundatie: "#e7c07a",
  fundatieLinie: "#c98a2b",
  pamant: "#e6ded1",
  pamantLinie: "#b9ab93",
  solicitare: "#b5323d",
  rezistenta: "#4f8207",
  apa: "#3d7cab",
  text: "#232323",
};

function Cadru({ children, w = 340, h = 150, titlu }: {
  children: React.ReactNode; w?: number; h?: number; titlu?: string;
}) {
  return (
    <figure className="dg-fig">
      <svg viewBox={`0 0 ${w} ${h}`} className="dg-svg" role="img">
        {children}
      </svg>
      {titlu && <figcaption className="dg-cap">{titlu}</figcaption>}
    </figure>
  );
}

const T = (props: React.SVGProps<SVGTextElement>) => (
  <text fontSize="9" fill={S.text} {...props} />
);

// ---------------------------------------------------------------- presiuni
// Cele trei regimuri ale tălpii: centrică, excentrică fără desprindere,
// excentrică cu desprindere. Ele explică de ce apare μ și ce e „aria activă".
export function DiagramaPresiuni() {
  useT();
  const cazuri = [
    { x: 8, tit: t("struct.rzv.centric"), sub: "e = 0", p1: 26, p2: 26, contact: 92 },
    { x: 122, tit: "e ≤ L/6", sub: t("struct.dgf.trapez"), p1: 40, p2: 12, contact: 92 },
    { x: 236, tit: "e > L/6", sub: t("struct.dgf.desprinde"), p1: 52, p2: 0, contact: 60 },
  ];
  return (
    <Cadru w={340} h={150}
           titlu={t("struct.dgf.distribuie_presiunea_sub")}>
      {cazuri.map((c) => (
        <g key={c.tit}>
          {/* talpa */}
          <rect x={c.x} y={40} width={92} height={10}
                fill={S.fundatie} stroke={S.fundatieLinie} />
          {/* forța verticală, deplasată cu excentricitatea */}
          <line x1={c.x + 46 + (c.p1 - c.p2) * 0.5} y1={16}
                x2={c.x + 46 + (c.p1 - c.p2) * 0.5} y2={38}
                stroke={S.solicitare} strokeWidth="1.5" markerEnd="url(#sg)" />
          <T x={c.x + 46 + (c.p1 - c.p2) * 0.5 + 3} y={22} fill={S.solicitare}>N</T>
          {/* diagrama de presiuni, sub talpă */}
          <polygon
            points={`${c.x + 92 - c.contact},50 ${c.x + 92},50 ${c.x + 92},${50 + c.p1} ${c.x + 92 - c.contact},${50 + c.p2}`}
            fill={S.rezistenta} fillOpacity="0.18" stroke={S.rezistenta} />
          <T x={c.x + 92 + 2} y={50 + c.p1} fill={S.rezistenta}>p₁</T>
          {c.p2 > 0
            ? <T x={c.x + 92 - c.contact - 12} y={50 + c.p2} fill={S.rezistenta}>p₂</T>
            : <T x={c.x - 2} y={62} fill={S.solicitare}>0</T>}
          {/* zona desprinsă */}
          {c.contact < 92 && (
            <>
              <line x1={c.x} y1={50} x2={c.x + 92 - c.contact} y2={50}
                    stroke={S.solicitare} strokeDasharray="3 2" />
              <T x={c.x} y={74} fill={S.solicitare}>{t("struct.dgf.desprins")}</T>
            </>
          )}
          <T x={c.x} y={122} fontWeight="600">{c.tit}</T>
          <T x={c.x} y={133} fill="#747474">{c.sub}</T>
        </g>
      ))}
      <defs>
        <marker id="sg" markerWidth="6" markerHeight="6" refX="3" refY="5"
                orient="auto">
          <path d="M0,0 L3,6 L6,0 Z" fill={S.solicitare} />
        </marker>
      </defs>
    </Cadru>
  );
}

// ---------------------------------------------------------------- lunecare
export function DiagramaLunecare() {
  useT();
  return (
    <Cadru w={300} h={130} titlu={t("struct.dgf.lunecarea_impinge_latera")}>
      <rect x={0} y={60} width={300} height={70} fill={S.pamant} />
      <line x1={0} y1={60} x2={300} y2={60} stroke={S.pamantLinie} />
      {/* fundatia ingropata */}
      <rect x={90} y={58} width={120} height={34}
            fill={S.fundatie} stroke={S.fundatieLinie} />
      {/* forta orizontala */}
      <line x1={40} y1={40} x2={110} y2={40} stroke={S.solicitare}
            strokeWidth="2" markerEnd="url(#dr)" />
      <T x={44} y={34} fill={S.solicitare} fontWeight="600">{t("struct.dgf.h_cutremur")}</T>
      {/* greutatea */}
      <line x1={150} y1={20} x2={150} y2={54} stroke={S.text}
            strokeWidth="1.5" markerEnd="url(#jos)" />
      <T x={154} y={30}>{t("struct.dgf.v_greutatea")}</T>
      {/* frecarea pe talpa */}
      {[...Array(6)].map((_, i) => (
        <line key={i} x1={98 + i * 20} y1={95} x2={110 + i * 20} y2={95}
              stroke={S.rezistenta} strokeWidth="1.5" markerEnd="url(#stg)" />
      ))}
      <T x={96} y={108} fill={S.rezistenta}>{t("struct.dgf.frecare_talpa_v")}</T>
      {/* rezistenta pasiva */}
      <polygon points="86,58 86,92 74,92" fill={S.rezistenta} fillOpacity="0.25"
               stroke={S.rezistenta} />
      <T x={4} y={78} fill={S.rezistenta}>{t("struct.dgf.impingerea")}</T>
      <T x={4} y={88} fill={S.rezistenta}>{t("struct.dgf.pasiva_r_p")}</T>
      <defs>
        <marker id="dr" markerWidth="6" markerHeight="6" refX="6" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill={S.solicitare} />
        </marker>
        <marker id="stg" markerWidth="6" markerHeight="6" refX="6" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill={S.rezistenta} />
        </marker>
        <marker id="jos" markerWidth="6" markerHeight="6" refX="3" refY="5" orient="auto">
          <path d="M0,0 L3,6 L6,0 Z" fill={S.text} />
        </marker>
      </defs>
    </Cadru>
  );
}

// -------------------------------------------------------------- rasturnare
export function DiagramaRasturnare() {
  useT();
  return (
    <Cadru w={300} h={150} titlu={t("struct.dgf.rasturnarea_rotirea_juru")}>
      <rect x={0} y={112} width={300} height={38} fill={S.pamant} />
      <line x1={0} y1={112} x2={300} y2={112} stroke={S.pamantLinie} />
      {/* cladirea */}
      <rect x={80} y={26} width={110} height={86}
            fill={S.beton} stroke={S.betonLinie} />
      <rect x={70} y={112} width={130} height={10}
            fill={S.fundatie} stroke={S.fundatieLinie} />
      {/* forta orizontala la z_CM */}
      <line x1={20} y1={66} x2={76} y2={66} stroke={S.solicitare}
            strokeWidth="2" markerEnd="url(#dr2)" />
      <T x={22} y={60} fill={S.solicitare} fontWeight="600">H</T>
      <line x1={200} y1={66} x2={200} y2={112} stroke={S.solicitare}
            strokeDasharray="2 2" />
      <T x={204} y={92} fill={S.solicitare}>z_CM</T>
      {/* greutatea */}
      <line x1={135} y1={40} x2={135} y2={108} stroke={S.rezistenta}
            strokeWidth="1.5" markerEnd="url(#jos2)" />
      <T x={139} y={56} fill={S.rezistenta} fontWeight="600">V</T>
      {/* muchia si bratul */}
      <circle cx={200} cy={122} r={3.5} fill={S.solicitare} />
      <T x={206} y={134} fill={S.solicitare}>{t("struct.dgf.muchie")}</T>
      <line x1={135} y1={130} x2={200} y2={130} stroke={S.rezistenta} />
      <T x={140} y={142} fill={S.rezistenta}>{t("struct.dgf.brat")}</T>
      <path d="M214,110 A22,22 0 0,1 210,130" fill="none"
            stroke={S.solicitare} strokeWidth="1.2" markerEnd="url(#dr2)" />
      <defs>
        <marker id="dr2" markerWidth="6" markerHeight="6" refX="6" refY="3" orient="auto">
          <path d="M0,0 L6,3 L0,6 Z" fill={S.solicitare} />
        </marker>
        <marker id="jos2" markerWidth="6" markerHeight="6" refX="3" refY="5" orient="auto">
          <path d="M0,0 L3,6 L6,0 Z" fill={S.rezistenta} />
        </marker>
      </defs>
    </Cadru>
  );
}

// ------------------------------------------------------------- subpresiune
export function DiagramaSubpresiune() {
  useT();
  return (
    <Cadru w={300} h={130} titlu={t("struct.dgf.subpresiunea_apa_impinge")}>
      <rect x={0} y={40} width={300} height={90} fill={S.pamant} />
      <rect x={0} y={62} width={300} height={68} fill={S.apa} fillOpacity="0.18" />
      <line x1={0} y1={62} x2={300} y2={62} stroke={S.apa} strokeDasharray="4 3" />
      <T x={4} y={58} fill={S.apa}>{t("struct.dgf.nivelul_apei")}</T>
      <line x1={0} y1={40} x2={300} y2={40} stroke={S.pamantLinie} />
      <T x={4} y={36}>CTN</T>
      {/* fundatia sub nivelul apei */}
      <rect x={100} y={86} width={110} height={26}
            fill={S.fundatie} stroke={S.fundatieLinie} />
      {/* greutatea */}
      <line x1={155} y1={54} x2={155} y2={82} stroke={S.rezistenta}
            strokeWidth="1.5" markerEnd="url(#jos3)" />
      <T x={159} y={70} fill={S.rezistenta}>{t("struct.dgf.g_lest")}</T>
      {/* subpresiunea */}
      {[...Array(5)].map((_, i) => (
        <line key={i} x1={112 + i * 22} y1={126} x2={112 + i * 22} y2={116}
              stroke={S.solicitare} strokeWidth="1.5" markerEnd="url(#sus3)" />
      ))}
      <T x={104} y={124} fill={S.solicitare}>{t("struct.dgf.w_h_apa")}</T>
      <defs>
        <marker id="jos3" markerWidth="6" markerHeight="6" refX="3" refY="5" orient="auto">
          <path d="M0,0 L3,6 L6,0 Z" fill={S.rezistenta} />
        </marker>
        <marker id="sus3" markerWidth="6" markerHeight="6" refX="3" refY="1" orient="auto">
          <path d="M0,6 L3,0 L6,6 Z" fill={S.solicitare} />
        </marker>
      </defs>
    </Cadru>
  );
}

// --------------------------------------------------------------- ridicare
export function DiagramaRidicare() {
  useT();
  return (
    <Cadru w={300} h={140} titlu={t("struct.dgf.ridicarea_stalpul_latura")}>
      <rect x={0} y={96} width={300} height={44} fill={S.pamant} />
      <line x1={0} y1={96} x2={300} y2={96} stroke={S.pamantLinie} />
      {/* fundatia */}
      <rect x={30} y={96} width={240} height={22}
            fill={S.fundatie} stroke={S.fundatieLinie} />
      {/* doi stalpi: unul comprimat, unul intins */}
      <rect x={54} y={40} width={16} height={56} fill={S.beton} stroke={S.betonLinie} />
      <rect x={230} y={40} width={16} height={56} fill={S.beton} stroke={S.betonLinie} />
      <line x1={62} y1={12} x2={62} y2={36} stroke={S.solicitare}
            strokeWidth="2" markerEnd="url(#sus4)" />
      <T x={20} y={12} fill={S.solicitare} fontWeight="600">{t("struct.dgf.n_lt_0")}</T>
      <line x1={238} y1={12} x2={238} y2={36} stroke={S.text}
            strokeWidth="2" markerEnd="url(#jos4)" />
      <T x={210} y={12}>N &gt; 0</T>
      {/* lestul: talpa + umplutura pe lungimea aferenta */}
      <rect x={34} y={80} width={60} height={16} fill={S.pamant}
            stroke={S.pamantLinie} strokeDasharray="3 2" />
      <T x={36} y={92} fill={S.rezistenta}>{t("struct.dgf.umplutura")}</T>
      <line x1={34} y1={128} x2={94} y2={128} stroke={S.rezistenta} />
      <T x={36} y={138} fill={S.rezistenta}>{t("struct.dgf.lungimea_aferenta")}</T>
      <defs>
        <marker id="sus4" markerWidth="6" markerHeight="6" refX="3" refY="1" orient="auto">
          <path d="M0,6 L3,0 L6,6 Z" fill={S.solicitare} />
        </marker>
        <marker id="jos4" markerWidth="6" markerHeight="6" refX="3" refY="5" orient="auto">
          <path d="M0,0 L3,6 L6,0 Z" fill={S.text} />
        </marker>
      </defs>
    </Cadru>
  );
}

// ------------------------------------------------------------ strapungere
export function DiagramaStrapungere() {
  useT();
  return (
    <Cadru w={300} h={140}
           titlu={t("struct.dgf.strapungerea_stalpul_tre")}>
      {/* talpa in sectiune */}
      <rect x={20} y={60} width={260} height={40}
            fill={S.fundatie} stroke={S.fundatieLinie} />
      {/* stalpul */}
      <rect x={136} y={20} width={28} height={40} fill={S.beton} stroke={S.betonLinie} />
      <line x1={150} y1={4} x2={150} y2={18} stroke={S.solicitare}
            strokeWidth="2" markerEnd="url(#jos5)" />
      <T x={155} y={14} fill={S.solicitare} fontWeight="600">N_Ed</T>
      {/* conul de forfecare la 2d */}
      <line x1={136} y1={60} x2={76} y2={100} stroke={S.solicitare}
            strokeDasharray="4 2" />
      <line x1={164} y1={60} x2={224} y2={100} stroke={S.solicitare}
            strokeDasharray="4 2" />
      <T x={200} y={78} fill={S.solicitare}>2d</T>
      {/* d */}
      <line x1={286} y1={60} x2={286} y2={94} stroke={S.text} />
      <T x={272} y={56}>d</T>
      {/* reactiunea terenului din interiorul conturului */}
      {[...Array(6)].map((_, i) => (
        <line key={i} x1={84 + i * 26} y1={118} x2={84 + i * 26} y2={104}
              stroke={S.rezistenta} strokeWidth="1.3" markerEnd="url(#sus5)" />
      ))}
      <T x={74} y={132} fill={S.rezistenta}>
        {t("struct.dgf.reactiunea_se_scade")}
      </T>
      <defs>
        <marker id="jos5" markerWidth="6" markerHeight="6" refX="3" refY="5" orient="auto">
          <path d="M0,0 L3,6 L6,0 Z" fill={S.solicitare} />
        </marker>
        <marker id="sus5" markerWidth="6" markerHeight="6" refX="3" refY="1" orient="auto">
          <path d="M0,6 L3,0 L6,6 Z" fill={S.rezistenta} />
        </marker>
      </defs>
    </Cadru>
  );
}

// ----------------------------------------------------------------- tasare
export function DiagramaTasare() {
  useT();
  const straturi = [
    { y: 46, h: 22, e: "E₁" },
    { y: 68, h: 30, e: "E₂" },
    { y: 98, h: 34, e: "E₃" },
  ];
  return (
    <Cadru w={320} h={150}
           titlu={t("struct.dgf.tasarea_felie_pamant")}>
      {/* talpa */}
      <rect x={110} y={32} width={100} height={12}
            fill={S.fundatie} stroke={S.fundatieLinie} />
      <T x={116} y={26} fill={S.solicitare}>p_net</T>
      {straturi.map((s, i) => (
        <g key={i}>
          <rect x={0} y={s.y} width={320} height={s.h}
                fill={S.pamant} fillOpacity={0.5 + i * 0.15} stroke={S.pamantLinie} />
          <T x={4} y={s.y + s.h / 2 + 3}>{s.e}</T>
        </g>
      ))}
      {/* bulbul de eforturi: se largeste si slabeste cu adancimea */}
      <path d="M110,44 C104,70 92,96 76,132 L244,132 C228,96 216,70 210,44 Z"
            fill={S.solicitare} fillOpacity="0.12" stroke={S.solicitare}
            strokeDasharray="3 2" />
      <T x={128} y={64} fill={S.solicitare}>σz = α₀ · p_net</T>
      <T x={120} y={92} fill={S.solicitare}>{t("struct.dgf.slabeste_adancimea")}</T>
      {/* limita zonei active */}
      <line x1={0} y1={132} x2={320} y2={132} stroke={S.solicitare} strokeWidth="1.4" />
      <T x={4} y={144} fill={S.solicitare}>
        {t("struct.dgf.zona_activa_inchide")}
      </T>
    </Cadru>
  );
}

// ---------------------------------------------------------------- Winkler
export function DiagramaWinkler() {
  useT();
  return (
    <Cadru w={320} h={140}
           titlu={t("struct.dgf.modelul_winkler_terenul")}>
      {/* stalpi */}
      {[60, 160, 260].map((x) => (
        <g key={x}>
          <rect x={x - 8} y={14} width={16} height={34}
                fill={S.beton} stroke={S.betonLinie} />
          <line x1={x} y1={2} x2={x} y2={12} stroke={S.solicitare}
                strokeWidth="2" markerEnd="url(#jos6)" />
        </g>
      ))}
      {/* grinda de fundare */}
      <rect x={20} y={48} width={280} height={16}
            fill={S.fundatie} stroke={S.fundatieLinie} />
      <T x={22} y={44} fill={S.fundatieLinie}>{t("struct.dgf.grinda_fundare_ei")}</T>
      {/* arcuri */}
      {[...Array(13)].map((_, i) => {
        const x = 32 + i * 22;
        return (
          <path key={i}
                d={`M${x},64 l4,4 l-8,4 l8,4 l-8,4 l8,4 l-4,4`}
                fill="none" stroke={S.rezistenta} strokeWidth="1.2" />
        );
      })}
      <line x1={20} y1={94} x2={300} y2={94} stroke={S.pamantLinie} strokeWidth="2" />
      <T x={22} y={108} fill={S.rezistenta}>
        {t("struct.dgf.fiecare_arc")}
      </T>
      <T x={22} y={122} fill="#747474">
        {t("struct.dgf.reactiunea_arc_aria")}
      </T>
      <defs>
        <marker id="jos6" markerWidth="6" markerHeight="6" refX="3" refY="5" orient="auto">
          <path d="M0,0 L3,6 L6,0 Z" fill={S.solicitare} />
        </marker>
      </defs>
    </Cadru>
  );
}

// --------------------------------------------------- grinda de fundare M/V
export function DiagramaGrindaFundare() {
  useT();
  return (
    <Cadru w={320} h={150}
           titlu={t("struct.dgf.grinda_fundare_incarcare")}>
      {/* stalpi trag in jos */}
      {[70, 250].map((x) => (
        <g key={x}>
          <rect x={x - 9} y={10} width={18} height={30}
                fill={S.beton} stroke={S.betonLinie} />
          <line x1={x} y1={0} x2={x} y2={8} stroke={S.solicitare}
                strokeWidth="2" markerEnd="url(#jos7)" />
        </g>
      ))}
      <rect x={20} y={40} width={280} height={14}
            fill={S.fundatie} stroke={S.fundatieLinie} />
      {/* presiunea de jos */}
      {[...Array(11)].map((_, i) => (
        <line key={i} x1={32 + i * 26} y1={74} x2={32 + i * 26} y2={58}
              stroke={S.rezistenta} strokeWidth="1.4" markerEnd="url(#sus7)" />
      ))}
      <T x={22} y={86} fill={S.rezistenta}>{t("struct.dgf.reactiunea_incarca_jos")}</T>
      {/* diagrama de moment: semn invers fata de o grinda obisnuita */}
      <path d="M20,112 C70,112 70,96 70,96 C120,96 140,128 160,128 C180,128 200,96 250,96 C250,96 250,112 300,112"
            fill="none" stroke={S.solicitare} strokeWidth="1.6" />
      <line x1={20} y1={112} x2={300} y2={112} stroke={S.text} strokeDasharray="3 2" />
      <T x={148} y={140} fill={S.solicitare}>{t("struct.dgf.m_camp_intinde")}</T>
      <T x={22} y={104} fill={S.solicitare}>{t("struct.dgf.m_reazem")}</T>
      <defs>
        <marker id="jos7" markerWidth="6" markerHeight="6" refX="3" refY="5" orient="auto">
          <path d="M0,0 L3,6 L6,0 Z" fill={S.solicitare} />
        </marker>
        <marker id="sus7" markerWidth="6" markerHeight="6" refX="3" refY="1" orient="auto">
          <path d="M0,6 L3,0 L6,6 Z" fill={S.rezistenta} />
        </marker>
      </defs>
    </Cadru>
  );
}

// ------------------------------------------------------- tasarea relativa
export function DiagramaTasareRelativa() {
  useT();
  return (
    <Cadru w={320} h={130}
           titlu={t("struct.dgf.tasarea_uniforma_strica")}>
      {[{ x: 10, dy: 0, et: t("struct.dgf.tasare_uniforma") },
        { x: 172, dy: 14, et: t("struct.dgf.tasare_diferentiala") }].map((c) => (
        <g key={c.x}>
          <rect x={c.x} y={20} width={130} height={44}
                fill={S.beton} stroke={S.betonLinie}
                transform={c.dy ? `skewY(${Math.atan(c.dy / 130) * 57.3})` : undefined} />
          <line x1={c.x} y1={70 + (c.dy ? 0 : 0)} x2={c.x + 130} y2={70 + c.dy}
                stroke={S.fundatieLinie} strokeWidth="4" />
          {c.dy > 0 && (
            <>
              <line x1={c.x + 130} y1={70} x2={c.x + 130} y2={70 + c.dy}
                    stroke={S.solicitare} strokeWidth="1.4" />
              <T x={c.x + 134} y={80} fill={S.solicitare}>Δs</T>
              <path d="M198,34 l10,16 M206,34 l-10,16" stroke={S.solicitare}
                    strokeWidth="1.2" />
            </>
          )}
          <T x={c.x} y={100} fontSize="8.5">{c.et}</T>
        </g>
      ))}
      <T x={10} y={122} fill={S.text}>
        {t("struct.dgf.criteriul_panta")}
      </T>
    </Cadru>
  );
}
